import { z } from 'zod';

/**
 * URL state for Taxonomy Studio, so a version, node, attribute or upload can be
 * linked. Bad values fall back to "absent" rather than throwing, so a hand-edited
 * URL still opens.
 */

/** `/taxonomy` layout: which version every page reads (`draft` | `live` | `<seq>`). */
export const taxonomyLayoutSearchSchema = z.object({
  // Coerced: the router's JSON search parser turns `?release=14` into a number.
  release: z.coerce
    .string()
    .regex(/^(draft|live|\d+)$/)
    .optional()
    .catch(undefined),
});

/** `/taxonomy/products`: the selected node and its detail tab. */
export const taxonomyProductsSearchSchema = z.object({
  node: z.coerce.number().int().positive().optional().catch(undefined),
  tab: z.enum(['attributes', 'children', 'history']).optional().catch(undefined),
});

/** `/taxonomy/attributes`: the attribute whose sheet is open and its tab. */
export const taxonomyAttributesSearchSchema = z.object({
  section: z.coerce.string().min(1).optional().catch(undefined),
  sheetTab: z.enum(['details', 'values', 'attachments', 'history']).optional().catch(undefined),
});

/** `/taxonomy/versions`: the selected release, the view and a change search. */
export const taxonomyVersionsSearchSchema = z.object({
  seq: z.coerce
    .string()
    .regex(/^(draft|\d+)$/)
    .optional()
    .catch(undefined),
  view: z.enum(['release', 'audit']).optional().catch(undefined),
  q: z.coerce.string().optional().catch(undefined),
});

/** `/taxonomy/uploads`: the upload under review and the chosen template. */
export const taxonomyUploadsSearchSchema = z.object({
  upload: z.coerce.string().min(1).optional().catch(undefined),
  template: z
    .enum(['nodes', 'sections', 'values', 'attachments', 'scopes', 'gmc_mappings'])
    .optional()
    .catch(undefined),
});

export type TaxonomyProductsSearch = z.infer<typeof taxonomyProductsSearchSchema>;
export type TaxonomyAttributesSearch = z.infer<typeof taxonomyAttributesSearchSchema>;
export type TaxonomyVersionsSearch = z.infer<typeof taxonomyVersionsSearchSchema>;
export type TaxonomyUploadsSearch = z.infer<typeof taxonomyUploadsSearchSchema>;
